import axiosInstance from '../../../config/axios';

async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error: any) {
      const isServerError = error?.response?.status >= 500;
      const shouldRetry = isServerError && attempt < maxRetries;

      if (!shouldRetry) {
        throw error;
      }

      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, Math.min(delay, 8000)));
    }
  }

  throw new Error('Failed after all retries');
}

export interface Position {
  id: number;
  work_area: number;
  position_key: string;
  label: string;
  allowed_employment_types?: string[];
  active: boolean;
  position_title_default?: string;
  duties_richtext_default?: string;
  default_compensation_type?: 'hourly' | 'salary' | '';
  default_hourly_rate?: number;
  default_salary_monthly_cents?: number;
  default_monthly_hours?: number;
  default_weekly_hours?: number;
  default_probation_months?: number;
  default_notice_weeks?: number;
  default_notice_text?: string;
  default_extra_leave_days_per_year?: number;
  default_benefits?: Record<string, boolean>;
}

export interface PositionCreatePayload {
  work_area: number;
  position_key: string;
  label: string;
  allowed_employment_types?: string[];
  active?: boolean;
  position_title_default?: string;
  duties_richtext_default?: string;
  default_compensation_type?: 'hourly' | 'salary' | '';
  default_hourly_rate?: number;
  default_salary_monthly_cents?: number;
  default_monthly_hours?: number;
  default_weekly_hours?: number;
  default_probation_months?: number;
  default_notice_weeks?: number;
  default_notice_text?: string;
  default_extra_leave_days_per_year?: number;
  default_benefits?: Record<string, boolean>;
}

export interface PositionUpdatePayload {
  position_key?: string;
  label?: string;
  allowed_employment_types?: string[];
  active?: boolean;
  position_title_default?: string;
  duties_richtext_default?: string;
  default_compensation_type?: 'hourly' | 'salary' | '';
  default_hourly_rate?: number;
  default_salary_monthly_cents?: number;
  default_monthly_hours?: number;
  default_weekly_hours?: number;
  default_probation_months?: number;
  default_notice_weeks?: number;
  default_notice_text?: string;
  default_extra_leave_days_per_year?: number;
  default_benefits?: Record<string, boolean>;
}

export interface PreviewDefaults {
  probation_months?: number | null;
  notice_text?: string | null;
  benefits?: Record<string, boolean>;
  extra_leave_days_per_year?: number | null;
  compensation_type?: 'hourly' | 'salary';
  min_wage_hourly?: number;
}

export interface PreviewSuggestion {
  template_id: string;
  score: number;
  explanation: string;
  is_any_binding?: boolean;
}

export interface SchemaFieldOption {
  value: string | number;
  label: string;
}

export interface SchemaField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'decimal' | 'date' | 'select' | 'text' | 'checkbox';
  default_value?: any;
  options?: SchemaFieldOption[];
  help_text?: string;
  profile_map?: string;
  editable_in?: 'wizard' | 'template';
  step_hint?: 'basic_info' | 'compensation' | 'clauses';
  constraints?: {
    min?: number;
    max?: number;
  };
}

export interface ContractSchema {
  required_fields: SchemaField[];
  optional_fields: SchemaField[];
}

export interface BlockSchemaField {
  block_key: string;
  label: string;
  type: 'richtext';
  editable_by: 'admin' | 'manager' | 'all';
  max_length?: number;
  default_content?: string;
  help_text?: string;
}

export interface PreviewResponse {
  suggestions: PreviewSuggestion[];
  defaults: PreviewDefaults;
  schema?: ContractSchema;
  blocks?: BlockSchemaField[];
}

export interface AnnexSelection {
  template_id: string;
  variables: Record<string, any>;
}

export interface BundleOptions {
  make_cover_page: boolean;
  make_combined_pdf: boolean;
}

export interface AttachmentPayload {
  template_id: string;
  variables?: Record<string, any>;
}

export interface ContractCreationPayload {
  template_id: string;
  patch_profile: boolean;
  profile_patch: Partial<{
    primary_work_area: number;
    employment_type: string;
    position_title: string;
    start_date: string;
    contract_end_date?: string | null;
    probation_end_date?: string | null;
    holiday_extra_days_per_year?: number | null;
    contract_hours_per_week?: number;
  }>;
  compensation?: {
    type: 'HOURLY' | 'SALARY';
    hourly_rate?: string;
    monthly_salary?: string;
    contract_hours_per_week?: number;
    monthly_hours?: number;
  };
  overrides?: Record<string, any>;
  blocks?: Record<string, string>;
  attachments?: AttachmentPayload[];
  bundle?: BundleOptions;
  idempotency_key?: string;
}

export interface BundleItem {
  kind: 'COVER' | 'MAIN' | 'ANNEX';
  order: number;
  document_id: number;
  title: string;
  pdf_url?: string | null;
}

export interface BundleMerge {
  success: boolean;
  warnings: string[];
}

export interface BundleResponse {
  id: number;
  attachments_count: number;
  combined_pdf_url?: string | null;
  suggested_file_name?: string;
  merge: BundleMerge;
}

export interface ContractCreationResponse {
  document_id: number;
  status: 'READY' | 'SENT' | 'CREATED' | 'ERROR';
  pdf_url?: string | null;
  bundle?: BundleResponse;
  trace_id?: string;
}

export async function fetchPositionsForWA(waId: number): Promise<Position[]> {
  const { data } = await axiosInstance.get<Position[]>(
    `/api/admin/work-areas/${waId}/positions/`,
    { params: { _ts: Date.now() } }
  );
  return data;
}

export async function createPosition(payload: PositionCreatePayload): Promise<Position> {
  const { data } = await axiosInstance.post<Position>(
    `/api/admin/positions/`,
    payload
  );
  return data;
}

export async function updatePosition(positionId: number, payload: PositionUpdatePayload): Promise<Position> {
  const { data } = await axiosInstance.patch<Position>(
    `/api/admin/positions/${positionId}/`,
    payload
  );
  return data;
}

export async function deletePosition(positionId: number): Promise<void> {
  await axiosInstance.delete(`/api/admin/positions/${positionId}/`);
}

export async function previewContract(
  employeeId: number,
  payload: {
    work_area_id?: number | null;
    employment_type?: string;
    position_key?: string | null;
    start_date?: string;
  }
): Promise<PreviewResponse> {
  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.post<PreviewResponse>(
      `/api/admin/employees/${employeeId}/contract-wizard/preview/`,
      payload
    );
    return data;
  });
}

export async function createContract(
  employeeId: number,
  payload: ContractCreationPayload
): Promise<ContractCreationResponse> {
  return fetchWithRetry(async () => {
    const response = await axiosInstance.post<Omit<ContractCreationResponse, 'trace_id'>>(
      `/api/admin/employees/${employeeId}/contracts/`,
      payload
    );
    const traceId = response.headers?.['x-trace-id'];
    return {
      ...response.data,
      trace_id: traceId,
    };
  });
}

export interface BundlePreviewPayload {
  template_id: number;
  variables: Record<string, any>;
  attachments?: Array<{
    template_id: string;
    variables?: Record<string, any>;
  }>;
}

export interface BundlePreviewResponse {
  main_contract: {
    template_id: number;
    html?: string;
  };
  annexes: Array<{
    template_id: number;
    template_code: string;
    html?: string;
  }>;
  combined_html?: string;
  pdf_url?: string;
  pdf_bytes_b64?: string;
}

export async function previewContractBundle(
  employeeId: number,
  payload: BundlePreviewPayload
): Promise<BundlePreviewResponse> {
  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.post<BundlePreviewResponse>(
      `/api/admin/employees/${employeeId}/contracts/preview-bundle/`,
      payload
    );
    return data;
  });
}
